'use client';

import { useId, useRef, useSyncExternalStore } from 'react';
import { ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { useStatusRolls } from '@/hooks/use-status-rolls';
import { useMoneyRules, type StatusRollRule, type StatusRollType } from '@/lib/money-rules';
import type { PublicPromotion } from '@/lib/promotion-content';
import type { ProductWithCategories } from '@/lib/api/products';
import { ProductPhoto, usePhotoEdges } from './offer-product';
import { getOfferPhoto } from './offer-photo-source';
import './mobile-status-showcase.css';

interface MobileStatusShowcaseProps {
  offer: PublicPromotion;
  onOpen: (trigger: HTMLButtonElement, tier: StatusRollType) => void;
}

const subscribeHydration = () => () => {};

const tierName = (tier: StatusRollType) => tier.charAt(0).toUpperCase() + tier.slice(1);
const price = (value: number) => `${new Intl.NumberFormat('ru-RU').format(value)}\u00a0₽`;

/** A preview of automatic order gifts; browsing never chooses a gift for the cart. */
export function MobileStatusShowcase({ offer, onOpen }: MobileStatusShowcaseProps) {
  const headingId = useId();
  const descriptionId = useId();
  const trackId = useId();
  const track = useRef<HTMLUListElement>(null);
  const rules = useMoneyRules();
  const status = useStatusRolls();
  const tiers = [...rules.status_rolls].sort((a, b) => a.min_sum - b.min_sum);

  const nextGift = () => {
    const rail = track.current;
    const card = rail?.querySelector<HTMLElement>('.mobile-status-card');
    if (!rail || !card) return;
    const step =
      card.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 16);
    const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
    rail.scrollTo({
      left: atEnd ? 0 : rail.scrollLeft + step,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      className="mobile-status-showcase"
      data-mobile-status-showcase
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
    >
      <header className="mobile-status-intro">
        <p className="mobile-status-eyebrow">Подарки к заказу</p>
        <h2 id={headingId}>{offer.title}</h2>
        <p id={descriptionId} className="mobile-status-description">
          {offer.description}
        </p>
        {tiers.length > 1 && (
          <button
            className="mobile-status-browse"
            type="button"
            onClick={nextGift}
            aria-controls={trackId}
          >
            Листать подарки <ArrowRight size={18} aria-hidden="true" />
          </button>
        )}
      </header>
      <ul
        ref={track}
        id={trackId}
        className="mobile-status-track"
        aria-label="Статусные подарки"
        tabIndex={0}
      >
        {tiers.map((rule) => (
          <StatusGift
            key={rule.type}
            rule={rule}
            product={status.rolls[rule.type]}
            loading={status.isLoading}
            onOpen={onOpen}
          />
        ))}
      </ul>
      <p className="mobile-status-footnote">
        В заказ добавится один подарок — за самый высокий достигнутый порог.
      </p>
    </section>
  );
}

function StatusGift({
  rule,
  product,
  loading,
  onOpen,
}: {
  rule: StatusRollRule;
  product?: ProductWithCategories;
  loading: boolean;
  onOpen: MobileStatusShowcaseProps['onOpen'];
}) {
  // The catalog may already be cached while this server placeholder hydrates.
  const hydrated = useSyncExternalStore(subscribeHydration, () => true, () => false);
  const displayProduct = hydrated ? product : undefined;
  const displayLoading = !hydrated || loading;
  const photo = getOfferPhoto(displayProduct);
  const scene = usePhotoEdges(photo);
  return (
    <li
      className="mobile-status-card"
      style={scene}
      data-catalog-product-id={displayProduct?.id}
      data-mobile-status-card={rule.type}
      data-mobile-status-type={rule.type}
    >
      <h3 className="mobile-status-tier">{tierName(rule.type)}</h3>
      <div className="mobile-status-photo">
        {displayProduct && photo ? (
          <ProductPhoto
            key={photo}
            src={photo}
            name={displayProduct.name}
            optimized
          />
        ) : (
          <span className="mobile-status-photo-message" role={displayLoading ? 'status' : undefined}>
            {displayLoading ? 'Загружаем подарок…' : 'Фото подарка недоступно'}
          </span>
        )}
      </div>
      <div className="mobile-status-card-info">
        <p className="mobile-status-threshold">
          <span>К заказу от</span>
          <strong>{price(rule.min_sum)}</strong>
        </p>
        <button
          className="mobile-status-open"
          type="button"
          data-mobile-status-open={rule.type}
          aria-haspopup="dialog"
          aria-label={`Условия подарка ${tierName(rule.type)}`}
          onClick={(event) => onOpen(event.currentTarget, rule.type)}
        >
          Условия <ArrowUpRight size={18} aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}
